import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@withjoy/joykit';
import { css, keyframes, styled } from '@withjoy/joykit';
import { withWindow } from '@shared/utils/withWindow';

type ProgressiveLoaderProps = Readonly<{
  isLoading: boolean;
}>;

type LoaderPhase = Readonly<'idle' | 'loading' | 'completing'>;

const RESET_DELAY_MS = 400;
const ANIMATION_DURATION_MS = 10000;

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 90%; }
`;

const StyledLoaderContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 4px;
  background-color: transparent;
  border-radius: 4px;
  overflow: hidden;
`;

const StyledProgressBar = styled(Box)<{ $phase: LoaderPhase }>`
  height: 100%;
  background-color: ${props => props.theme.colors.newUIBrandPrimary};
  width: 0%;
  transition:
    width 0.3s ease-out,
    opacity 0.3s ease-out;

  ${props =>
    props.$phase === 'loading' &&
    css`
      animation: ${progressAnimation} ${ANIMATION_DURATION_MS}ms ease-in-out forwards;
    `}

  ${props =>
    props.$phase === 'completing' &&
    css`
      animation: none;
      width: 100%;
      opacity: 0;
    `}
`;

export const ProgressiveLoader = ({ isLoading }: ProgressiveLoaderProps) => {
  const [phase, setPhase] = useState<LoaderPhase>('idle');
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    let timerId: number | undefined;

    if (isLoading) {
      setPhase('loading');
    } else if (wasLoadingRef.current) {
      setPhase('completing');
      withWindow(global => {
        timerId = global.setTimeout(() => setPhase('idle'), RESET_DELAY_MS);
      });
    }

    wasLoadingRef.current = isLoading;

    return () => {
      if (timerId !== undefined) {
        withWindow(global => global.clearTimeout(timerId));
      }
    };
  }, [isLoading]);

  if (phase === 'idle') {
    return null;
  }

  return (
    <StyledLoaderContainer>
      <StyledProgressBar $phase={phase} />
    </StyledLoaderContainer>
  );
};
